// Pure helpers for the ADMIN "Create invoice" feature (Orders tab and Customers panel).
// No React, no Supabase, no localStorage: everything here is unit-tested.
//
// What an admin-created invoice IS (decisions D2/D3):
//   id        INV-A + YYMMDD (local date) + 6 random [A-Z0-9]: never INV-F (rep invoice), never INV-<digits>
//             (dealer checkout). 17 characters, so a Square idempotency key (orderId + 21) stays under 45.
//   source    "admin_invoice"  (so none of the rep-money / rep-stock / remittance / rep-adjust code, which
//             is keyed on repCreated, the INV-F prefix or source "rep_invoice", ever treats it as a rep invoice)
//   status    "outstanding"
//   user_id   the dealer's FRESH profiles.id (UUID); customer = the FRESH profiles.company EXACTLY (the
//             dealer Invoices page matches the name exactly)
//   rep       the credited rep's canonical profiles.full_name, or the HOUSE_REP_LABEL sentinel. NEVER an
//             empty string: an empty rep is re-filled by savePortalOrder and, in repMetrics, falls back to
//             the customer's assigned rep, so "no commission" could never be expressed with it.
//   items     UNIT shape: { sku, name, qty: units, price: CATALOG unit price, units: qty, case: 1 }
//             (+ parentSku / variationLabel for a variation). qty IS units, exactly like the rep builder.
//   money     computePricing (invoiceDiscount.js): the discount is a separate line subtracted once from the
//             catalog subtotal; total is the NET amount that is charged, commissioned and refunded.
import { computePricing, normalizePct, samePct, round2, isUuid, formatPct, DISCOUNT_LABEL } from "./invoiceDiscount";
import { repNamesMatch } from "./repMetrics";
import { flattenCatalogForPicker, isRepCreatedInvoice } from "./inventoryMath";
import { deriveAccountStatus } from "./accountLifecycle";

export const ADMIN_INVOICE_SOURCE = "admin_invoice";
export const ADMIN_INVOICE_STATUS = "outstanding";
// Stamped in portal_orders.rep for an invoice nobody earns commission on. It starts with a non-letter, so
// neither repNamesMatch nor namesMatchFuzzy can ever equal a real person, and it is non-empty, so the
// customer-name fallback in filterRepInvoices is never reached.
export const HOUSE_REP_LABEL = "(House account - no rep)";
// What the printed / emailed invoice shows in its rep line for a house account.
export const HOUSE_REP_DISPLAY = "MotorPower Care";
export const ADMIN_INVOICE_ID_RE = /^INV-A\d{6}[A-Z0-9]{6}$/;
// Sanity limits so an absurd number can never reach a numeric overflow in the database.
export const MAX_UNITS_PER_LINE = 100000;
export const MAX_INVOICE_TOTAL = 10000000;   // the invoice total must be BELOW this

const ID_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

/** INV-A + YYMMDD (local date) + 6 random [A-Z0-9]. `now` and `rnd` are injectable for tests. */
export function makeAdminInvoiceId(now, rnd) {
  const d = now instanceof Date && !isNaN(now.getTime()) ? now : new Date();
  const random = typeof rnd === "function" ? rnd : Math.random;
  const two = (n) => String(n).padStart(2, "0");
  const stamp = two(d.getFullYear() % 100) + two(d.getMonth() + 1) + two(d.getDate());
  let tail = "";
  for (let i = 0; i < 6; i += 1) {
    const r = Number(random());
    const idx = Number.isFinite(r) ? Math.min(ID_ALPHABET.length - 1, Math.max(0, Math.floor(r * ID_ALPHABET.length))) : 0;
    tail += ID_ALPHABET.charAt(idx);
  }
  return "INV-A" + stamp + tail;
}

export const isAdminInvoiceId = (id) => ADMIN_INVOICE_ID_RE.test(String(id == null ? "" : id));
export const isAdminInvoice = (inv) => !!inv && inv.source === ADMIN_INVOICE_SOURCE;

/** Is this cached invoice one of THIS dealer's? By exact company name (as it always was), plus: an OFFICE invoice is
 *  also tied to the dealer by account id, so it still counts after the company name was edited. The id match is
 *  limited to office invoices, so no older rep / portal invoice newly appears. ONE rule for the dealer Invoices page
 *  and for the dealer's due-tomorrow reminders, so the two can never disagree. */
export const isInvoiceOfDealer = (inv, customerName, userId) => !!inv && (inv.customer === customerName || (!!userId && inv.userId === userId && inv.source === ADMIN_INVOICE_SOURCE));

/** Was this invoice MADE FOR the dealer by the office or by their sales rep (as opposed to the dealer's own checkout
 *  order)? "Made by the rep" is the app's one existing rule, isRepCreatedInvoice (inventoryMath.js): the repCreated flag
 *  (the rep's own device), OR source "rep_invoice", OR an id starting INV-F (what the dealer's own device gets when it
 *  hydrates a rep invoice from portal_orders, with NO repCreated flag). ONE rule for the dealer Invoices page's "new
 *  invoices" banner, its wording, the row highlight and "Mark seen", so the four can never disagree. */
export const isMadeByRep = (inv) => !!inv && inv.source !== ADMIN_INVOICE_SOURCE && isRepCreatedInvoice(inv);
export const isMadeByOfficeOrRep = (inv) => isAdminInvoice(inv) || isMadeByRep(inv);

/** The invoice date text used everywhere in the app ("Oct 20, 2026"). */
export function invoiceDateText(d) {
  const dt = d instanceof Date ? d : new Date(d);
  return (isNaN(dt.getTime()) ? new Date() : dt).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

// ── who can be invoiced ─────────────────────────────────────────────────────────
const blank = (s) => !String(s == null ? "" : s).trim() || String(s).trim() === "—";

/** A LIVE, approved dealer account with a UUID profile id and a real company name. Not a rep, not an
 *  admin, not paused / deleted / pending / rejected (account.status is Supabase-derived), not a demo
 *  (INIT_ACCOUNTS) account. This is the list-level check; the modal re-checks a FRESH profile. */
export function isEligibleDealerAccount(a) {
  if (!a || typeof a !== "object") return false;
  if (a.isLive !== true) return false;
  if (!isUuid(String(a.supabaseId || ""))) return false;
  if (a.status !== "approved") return false;
  if (a.role === "rep" || a.tier === "rep" || a.role === "admin") return false;
  if (blank(a.company)) return false;
  return true;
}

export function eligibleDealerAccounts(accounts) {
  return (Array.isArray(accounts) ? accounts : []).filter(isEligibleDealerAccount)
    .slice().sort((x, y) => String(x.company).localeCompare(String(y.company), undefined, { sensitivity: "base" }));
}

/** The same check on a FRESH profiles row (snake_case, straight from the database). Returns null when
 *  the dealer can be invoiced, otherwise the reason (shown to the admin). */
export function freshProfileProblem(profile, expectedId) {
  if (!profile || typeof profile !== "object") return "The dealer account could not be read.";
  if (expectedId && String(profile.id) !== String(expectedId)) return "The database returned a different account than the one selected.";
  if (!isUuid(String(profile.id || ""))) return "This account has no valid account id.";
  if (profile.role === "rep" || profile.role === "admin") return "This account is not a dealer account.";
  const st = profileStatus(profile);
  if (st !== "approved") return st === "paused" || st === "deleted"
    ? "This account is " + st + " and cannot be invoiced."
    : "This account is not approved yet (it is " + st + ") and cannot be invoiced.";
  if (blank(profile.company)) return "This account has no company name, so the dealer would not see the invoice under their name. Add a company name in Edit account first.";
  return null;
}

// The ONE lifecycle rule of the app (accountLifecycle.deriveAccountStatus): profiles.status deleted / paused
// wins; a leftover deleted_at / paused_at only counts for a legacy-stripped row (rejected and not approved), so
// an applicant who was later approved is "approved" here exactly as everywhere else.
function profileStatus(p) {
  return deriveAccountStatus(p);
}

export const validEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || "").trim());

// ── payment terms ───────────────────────────────────────────────────────────────
// The stored label uses the REP vocabulary ("Net 30"), which getNetDays parses for due dates, overdue
// pills and reminders; the dealer vocabulary ("Net 30 terms") parses too, but one vocabulary is kept.
export const TERMS_STORED_LABEL = { cc: "Credit card", ach: "ACH", net15: "Net 15", net30: "Net 30", net60: "Net 60", check: "Check", wire: "Wire transfer" };
export const termsLabelForId = (id) => TERMS_STORED_LABEL[String(id)] || "";

/** Any stored shape (array, JSON string, CSV) -> an array of term ids (strings). */
export function normalizeTermIds(v) {
  let list = null;
  if (Array.isArray(v)) list = v;
  else if (typeof v === "string" && v.trim()) {
    const s = v.trim();
    if (s.startsWith("[")) {
      try { const a = JSON.parse(s); if (Array.isArray(a)) list = a; }
      catch (e) { console.warn("[adminInvoicePure] normalizeTermIds: the stored payment terms are not valid JSON, reading them as a comma-separated list instead:", e?.message || e); list = null; }
    }
    if (!list) list = s.split(",");
  }
  return (list || []).map((x) => String(x == null ? "" : x).trim()).filter(Boolean);
}

/** The terms the admin may pick: the account's approved terms (settings first, then the profile), in the
 *  order of allTerms ([{id,label}]). With none approved on file every term is offered and `fallback` is
 *  true so the form can say so. */
export function termsOptionsFor(approvedSettings, approvedProfile, allTerms) {
  const all = (Array.isArray(allTerms) ? allTerms : []).filter((t) => t && TERMS_STORED_LABEL[t.id]);
  const known = new Set(all.map((t) => t.id));
  const fromSettings = normalizeTermIds(approvedSettings).filter((id) => known.has(id));
  const fromProfile = normalizeTermIds(approvedProfile).filter((id) => known.has(id));
  const ids = fromSettings.length ? fromSettings : fromProfile;
  const fallback = ids.length === 0;
  const picked = fallback ? all : all.filter((t) => ids.includes(t.id));
  return { options: picked.map((t) => ({ id: t.id, display: t.label, stored: TERMS_STORED_LABEL[t.id] })), fallback };
}

export function defaultTermsId(options) {
  const list = Array.isArray(options) ? options : [];
  if (list.some((o) => o.id === "net30")) return "net30";
  return list.length ? list[0].id : "";
}

// ── the credited rep ───────────────────────────────────────────────────────────
const lc = (s) => String(s == null ? "" : s).trim().toLowerCase().replace(/\s+/g, " ");

/** Resolve the dealer's assigned rep text (customer_settings.rep, free text such as "Marcus T.") to ONE
 *  rep option. Exact name first, then a UNIQUE repNamesMatch. Unresolved or ambiguous means NO default:
 *  the admin must choose explicitly. Never guesses. */
export function resolveCreditedRep(assignedName, repOptions) {
  const name = String(assignedName == null ? "" : assignedName).trim();
  if (!name || lc(name) === lc(HOUSE_REP_LABEL)) return { ok: false, reason: "unassigned" };
  const reps = (Array.isArray(repOptions) ? repOptions : []).filter((r) => r && !blank(r.name));
  const exact = reps.filter((r) => lc(r.name) === lc(name));
  if (exact.length === 1) return { ok: true, rep: exact[0], how: "exact" };
  if (exact.length > 1) return { ok: false, reason: "ambiguous", candidates: exact };
  const fuzzy = reps.filter((r) => repNamesMatch(name, r.name));
  if (fuzzy.length === 1) return { ok: true, rep: fuzzy[0], how: "fuzzy" };
  if (fuzzy.length > 1) return { ok: false, reason: "ambiguous", candidates: fuzzy };
  return { ok: false, reason: "none" };
}

/** The stamp stored in portal_orders.rep: the FRESH rep profile's full_name, or the house sentinel.
 *  Never an empty string. */
export function repStamp(choice, freshRepProfile) {
  if (choice && choice.mode === "house") return { ok: true, stamp: HOUSE_REP_LABEL, house: true };
  const name = String(freshRepProfile && freshRepProfile.full_name != null ? freshRepProfile.full_name : "").trim();
  if (!name || blank(name)) return { ok: false, error: "The selected sales rep has no name on file." };
  return { ok: true, stamp: name, house: false };
}

/** The name an emailed / printed invoice shows in its rep line: the real rep, or "MotorPower Care" for
 *  the house account. Never the sentinel, never undefined. */
export function repDisplayName(rep) {
  const r = String(rep == null ? "" : rep).trim();
  if (!r || r === HOUSE_REP_LABEL) return HOUSE_REP_DISPLAY;
  return r;
}

/** For every screen / print / email that RENDERS or SENDS a stored `inv.rep`: the internal house-account
 *  sentinel becomes "MotorPower Care"; every other value (including an empty one) is returned UNCHANGED, so
 *  existing invoices render exactly as before. Never use this to compare or to write a stamp. */
export function displayRepStamp(rep) {
  return String(rep == null ? "" : rep).trim() === HOUSE_REP_LABEL ? HOUSE_REP_DISPLAY : rep;
}

/** The OTHER active reps whose name fuzzy-matches the chosen rep's (repNamesMatch: same first name and last
 *  initial). Commission and "my invoices" use that fuzzy match, so an invoice stamped with the chosen rep's
 *  name would also show for these reps. Used for a warning only; repMetrics is not changed. */
export function similarNamedReps(chosen, repOptions) {
  if (!chosen || blank(chosen.name)) return [];
  return (Array.isArray(repOptions) ? repOptions : []).filter((r) => r && r.id !== chosen.id && !blank(r.name) && repNamesMatch(chosen.name, r.name));
}

/** Fresh rep profile problem (null = can be credited). */
export function freshRepProblem(profile, expectedId) {
  if (!profile || typeof profile !== "object") return "The sales rep account could not be read.";
  if (expectedId && String(profile.id) !== String(expectedId)) return "The database returned a different account than the selected sales rep.";
  if (profile.role !== "rep") return "The selected account is not a sales rep.";
  const st = profileStatus(profile);
  if (st !== "approved") return "The selected sales rep is " + (st === "paused" || st === "deleted" ? st : "not approved") + " and cannot be credited.";
  return null;
}

// ── the catalog ───────────────────────────────────────────────────────────────
/** Products + kits (the rows of portal_products, or SHARED_PRODUCTS.getActive()/getActiveKits()) ->
 *  { list, bySku }. Active only; a product with variations is replaced by its variation lines (exactly
 *  the rep invoice picker); kits are kept as one line each. NEVER falls back to seed data. */
export function buildCatalogLookup(products, kits) {
  const activeProducts = (Array.isArray(products) ? products : []).filter((p) => p && p.active !== false);
  const activeKits = (Array.isArray(kits) ? kits : []).filter((k) => k && k.active !== false).map((k) => ({ ...k, isKit: true }));
  const flat = [...flattenCatalogForPicker(activeProducts), ...activeKits];
  const list = [];
  const bySku = {};
  flat.forEach((p) => {
    const sku = String(p && p.sku != null ? p.sku : "").trim();
    if (!sku || bySku[sku]) return;
    const row = { sku, name: String(p.name || ""), price: round2(Number(p.price) || 0), isKit: !!p.isKit };
    if (p.parentSku) row.parentSku = p.parentSku;
    if (p.variationLabel) row.variationLabel = p.variationLabel;
    bySku[sku] = row;
    list.push(row);
  });
  return { list, bySku };
}

/** lines [{sku, qty}] + bySku -> { ok, items, problems }. UNIT shape (see the header). Every problem
 *  names its SKU. Nothing is guessed: an unknown SKU or a non-integer / < 1 quantity is a problem. */
export function buildAdminInvoiceItems(lines, bySku) {
  const problems = [];
  const items = [];
  const list = Array.isArray(lines) ? lines : [];
  if (list.length === 0) problems.push({ sku: "", reason: "Add at least one item." });
  list.forEach((l, i) => {
    const sku = String(l && l.sku != null ? l.sku : "").trim();
    if (!sku) { problems.push({ sku: "", reason: "Line " + (i + 1) + " has no product selected." }); return; }
    const row = bySku && bySku[sku];
    if (!row) { problems.push({ sku, reason: sku + " is not in the current product catalog." }); return; }
    const qtyNum = Number(l.qty);
    if (!Number.isInteger(qtyNum) || qtyNum < 1) { problems.push({ sku, reason: "Quantity for " + sku + " must be a whole number of units, 1 or more." }); return; }
    if (qtyNum > MAX_UNITS_PER_LINE) { problems.push({ sku, reason: "Quantity for " + sku + " cannot be more than " + MAX_UNITS_PER_LINE.toLocaleString("en-US") + " units on one line." }); return; }
    const item = { sku: row.sku, name: row.name, qty: qtyNum, price: row.price, units: qtyNum, case: 1 };
    if (row.parentSku) item.parentSku = row.parentSku;
    if (row.variationLabel) item.variationLabel = row.variationLabel;
    items.push(item);
  });
  return { ok: problems.length === 0, items, problems };
}

/** Catalog prices the admin SAW vs the fresh catalog: returns [{sku, was, now|null}] for every line SKU
 *  whose price changed or that disappeared. */
export function catalogDrift(lines, shownBySku, freshBySku) {
  const out = [];
  const seen = new Set();
  (Array.isArray(lines) ? lines : []).forEach((l) => {
    const sku = String(l && l.sku != null ? l.sku : "").trim();
    if (!sku || seen.has(sku)) return;
    seen.add(sku);
    const was = shownBySku && shownBySku[sku] ? round2(shownBySku[sku].price) : null;
    const f = freshBySku && freshBySku[sku];
    if (!f) { out.push({ sku, was, now: null }); return; }
    if (was == null || round2(f.price) !== was) out.push({ sku, was, now: round2(f.price) });
  });
  return out;
}

// ── the discount ───────────────────────────────────────────────────────────────
/** What the admin typed in the percent box -> { ok, pct } or { ok:false, error }. Decimals allowed.
 *  Empty, non-numeric, negative and >= 100 are refused (100% would make a free invoice). */
export function validatePercentInput(raw) {
  const s = String(raw == null ? "" : raw).trim();
  if (!s) return { ok: false, error: "Enter the discount percent (0 for no discount)." };
  const n = Number(s);
  if (!Number.isFinite(n)) return { ok: false, error: "The discount percent must be a number." };
  if (n < 0) return { ok: false, error: "The discount percent cannot be negative." };
  if (n >= 100) return { ok: false, error: "The discount percent must be below 100." };
  const pct = normalizePct(n);
  if (pct >= 100) return { ok: false, error: "The discount percent must be below 100." };
  return { ok: true, pct };
}

function parseRateField(raw, where) {
  if (raw == null || raw === "") return { ok: true, pct: 0 };
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0 || n >= 100 || normalizePct(n) >= 100) {
    return { ok: false, error: "The discount stored in " + where + " (" + String(raw) + ") is not a valid percentage. Correct it in Edit account first." };
  }
  return { ok: true, pct: normalizePct(n) };
}

/** The account's rate. customer_settings.discount is the PRIMARY source whenever its row exists: the same
 *  source the rep invoice builder (PR #126, getCustomerDiscountStrict) and the dealer's own checkout use.
 *  The profile value is the secondary one, shown beside it; it is only the pre-fill when the settings row
 *  is MISSING (`settingsFound === false`). Both fields are read and validated (never clamped).
 *  `needsAck` is true when the two disagree or the settings row is missing: the form then makes the admin
 *  confirm the rate (or type their own) before the Create buttons enable. */
export function accountRateFrom(profileDiscount, settingsDiscount, settingsFound) {
  const p = parseRateField(profileDiscount, "the account profile");
  if (!p.ok) return p;
  const s = parseRateField(settingsDiscount, "the customer settings");
  if (!s.ok) return s;
  const found = settingsFound !== false;   // a caller that does not know assumes the row exists
  const pct = found ? s.pct : p.pct;
  const source = pct > 0 ? (found ? "settings" : "profile") : "none";
  const disagree = !samePct(p.pct, s.pct);
  return { ok: true, pct, source, profilePct: p.pct, settingsPct: s.pct, settingsFound: found, disagree, needsAck: disagree || !found };
}

export const sourceText = (source) => (source === "profile" ? "the account profile" : source === "settings" ? "the customer settings" : "no discount set on the account");

/** The one place the invoice numbers come from: PR 1's computePricing. */
export const computeAdminPricing = (items, pct) => computePricing(items, pct);

/** null when the pricing can be saved, otherwise why not. */
export function pricingProblem(pricing) {
  if (!pricing || !(pricing.subtotal > 0)) return "The invoice total must be more than $0.00. Add items with a price.";
  if (!(pricing.total > 0)) return "This discount would make the invoice total $0.00. Lower the discount percent.";
  if (!(pricing.subtotal < MAX_INVOICE_TOTAL)) return "The invoice total must be below $" + MAX_INVOICE_TOTAL.toLocaleString("en-US") + ". Check the quantities.";
  return null;
}

// ── the stored row / the cache invoice ───────────────────────────────────────────
/** portal_orders INSERT payload. Built from FRESH values only (the caller passes the fresh profile). The
 *  three discount columns (migration 0034) are present ONLY when the percent is above 0. */
export function buildPortalOrderPayload({ invoiceId, profile, rep, paymentTerms, items, pricing }) {
  const payload = {
    user_id: profile.id,
    total: pricing.total,
    status: ADMIN_INVOICE_STATUS,
    order_ref: invoiceId,
    invoice_ref: invoiceId,
    customer: profile.company,
    customer_email: profile.email || "",
    payment_terms: paymentTerms,
    rep,
    items: JSON.stringify(items),
    source: ADMIN_INVOICE_SOURCE,
  };
  if (pricing.discount && pricing.discount.pct > 0) {
    payload.subtotal = pricing.subtotal;
    payload.discount_pct = pricing.discount.pct;
    payload.discount_amount = pricing.discount.amount;
  }
  return payload;
}

const itemKey = (i) => [String(i && i.sku), Number(i && i.qty) || 0, round2(i && i.price)].join("|");

function parseItemsField(v, where, ref) {
  if (Array.isArray(v)) return v;
  if (v == null || v === "") return [];
  try { const a = typeof v === "string" ? JSON.parse(v) : v; return Array.isArray(a) ? a : null; }
  catch (e) { console.warn("[adminInvoicePure] the items of", where, ref, "are not valid JSON:", e?.message || e); return null; }
}

const textOf = (v) => String(v == null ? "" : v).trim();
export const pctText = (pct) => (normalizePct(pct) > 0 ? formatPct(pct) + "%" : "none");
const money2 = (v) => "$" + round2(v).toFixed(2);

/** How an existing portal_orders row differs from what this payload would insert: a list of plain-English
 *  differences, EMPTY when the row is the same invoice. Compared: invoice number, dealer (user id, name,
 *  email), credited rep, source, payment terms, items (sku / units / unit price), subtotal, discount percent,
 *  discount amount and total. */
export function orderContentDifferences(row, payload) {
  if (!row || !payload) return ["the stored invoice could not be read"];
  const diffs = [];
  const ref = textOf(payload.invoice_ref);
  if (textOf(row.invoice_ref) !== ref) diffs.push("invoice number");
  if (Math.abs(round2(row.total) - round2(payload.total)) > 0.005) diffs.push("total (stored " + money2(row.total) + ", this attempt " + money2(payload.total) + ")");
  if (textOf(row.user_id) !== textOf(payload.user_id)) diffs.push("dealer account");
  if (textOf(row.customer) !== textOf(payload.customer)) diffs.push("dealer name (stored " + textOf(row.customer) + ", this attempt " + textOf(payload.customer) + ")");
  if (textOf(row.customer_email).toLowerCase() !== textOf(payload.customer_email).toLowerCase()) diffs.push("dealer email (stored " + (textOf(row.customer_email) || "none") + ", this attempt " + (textOf(payload.customer_email) || "none") + ")");
  if (textOf(row.rep) !== textOf(payload.rep)) diffs.push("credited rep (stored " + displayRepStamp(textOf(row.rep)) + ", this attempt " + displayRepStamp(textOf(payload.rep)) + ")");
  if (textOf(row.source) !== textOf(payload.source)) diffs.push("source");
  if (textOf(row.payment_terms) !== textOf(payload.payment_terms)) diffs.push("payment terms (stored " + (textOf(row.payment_terms) || "none") + ", this attempt " + (textOf(payload.payment_terms) || "none") + ")");
  const rows = parseItemsField(row.items, "the stored invoice", ref);
  const want = parseItemsField(payload.items, "the new invoice", ref);
  if (!rows || !want || rows.length !== want.length) diffs.push("items");
  else if (want.some((w, i) => itemKey(rows[i]) !== itemKey(w))) diffs.push("items");
  if (!samePct(row.discount_pct, payload.discount_pct)) diffs.push("discount percent (stored " + pctText(row.discount_pct) + ", this attempt " + pctText(payload.discount_pct) + ")");
  if (Math.abs(round2(row.discount_amount) - round2(payload.discount_amount)) > 0.005) diffs.push("discount amount");
  if (Math.abs(round2(row.subtotal) - round2(payload.subtotal)) > 0.005) diffs.push("subtotal (stored " + money2(row.subtotal) + ", this attempt " + money2(payload.subtotal) + ")");
  return diffs;
}

/** Does an existing portal_orders row hold exactly what this payload would insert? Used by the duplicate
 *  guard: an identical row is the same invoice (idempotent), anything else is a hard error. */
export function sameOrderContent(row, payload) {
  if (!row || !payload) return false;
  return orderContentDifferences(row, payload).length === 0;
}

/** For a DEDUPED save the screen, the cache copy and the email are built from the STORED row, never from the
 *  new attempt: this returns the stored values (falling back to `current` only for a field the row lacks). */
export function storedViewFromRow(row, current) {
  const cur = current || {};
  const r = row || {};
  const items = parseItemsField(r.items, "the stored invoice", r.invoice_ref);
  const pct = Number(r.discount_pct) || 0;
  let pricing = cur.pricing;
  if (Number.isFinite(Number(r.total)) && r.total != null) {
    pricing = pct > 0
      ? { subtotal: round2(r.subtotal), discount: { pct: normalizePct(pct), amount: round2(r.discount_amount), label: DISCOUNT_LABEL }, total: round2(r.total) }
      : { subtotal: round2(r.total), discount: null, total: round2(r.total) };
  }
  return {
    paymentTerms: textOf(r.payment_terms) || cur.paymentTerms,
    items: items && items.length ? items : cur.items,
    pricing,
    rep: textOf(r.rep) || cur.rep,
    customerEmail: r.customer_email != null && textOf(r.customer_email) ? textOf(r.customer_email) : undefined,
  };
}

/** The SHARED_INVOICES (cache) copy of a created invoice. Never carries repCreated, repId, repEmail,
 *  deliveryMethod or deliveredByRep. `row` is the stored portal_orders row (its created_at gives the
 *  date every other device will show). */
export function buildCacheInvoice({ invoiceId, profile, rep, paymentTerms, items, pricing, row, now, customerEmail }) {
  const inv = {
    id: invoiceId,
    orderId: invoiceId,
    date: invoiceDateText(row && row.created_at ? row.created_at : (now || new Date())),
    customer: profile.company,
    customerEmail: customerEmail != null && customerEmail !== "" ? customerEmail : (profile.email || ""),
    userId: profile.id,
    items,
    total: pricing.total,
    status: ADMIN_INVOICE_STATUS,
    paymentTerms,
    rep,
    source: ADMIN_INVOICE_SOURCE,
    fromDb: true,
    shippingStatus: "pending",
    customerInfo: { address: profile.address || "", city: "", phone: profile.phone || "", email: profile.email || "", ein: profile.ein || "", resaleLicense: "" },
  };
  if (pricing.discount && pricing.discount.pct > 0) { inv.subtotal = pricing.subtotal; inv.discount = pricing.discount; }
  return inv;
}

/** The payload of emailInvoice (api/send-email.js type "invoice"). The rep line is the real rep or
 *  "MotorPower Care", never the sentinel; discount fields only for a discounted invoice. */
export function buildInvoiceEmailPayload({ invoice, mpc }) {
  const base = {
    customerName: invoice.customer,
    customerEmail: invoice.customerEmail,
    invoiceId: invoice.id,
    total: Number(invoice.total).toFixed(2),
    repName: repDisplayName(invoice.rep),
    date: invoice.date,
    paymentTerms: invoice.paymentTerms,
    items: invoice.items,
    notes: "",
    customerAddress: (invoice.customerInfo && invoice.customerInfo.address) || "",
    customerPhone: (invoice.customerInfo && invoice.customerInfo.phone) || "",
    resaleLicense: "",
    mpc,
  };
  if (invoice.discount && invoice.discount.pct > 0) { base.subtotal = invoice.subtotal; base.discount = invoice.discount; }
  return base;
}
